import * as React from 'react';
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/forms/ui/select';
import { Card } from '@/components/forms/ui/card';
import {
  LineItemApiCreateRequest,
  ProposalLineItemApiUpdateRequest
} from 'shared/src/api/line-item-api-types';
import { LineItemType } from 'shared/src/domain/line-item/line-item-types';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { LineItemTypeIndicator } from '@/features/line-items/line-item-form/ui/line-item-type-indicator';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { useProposalContext } from '@/features/proposals/proposal-context';
import { useProposalInfo } from '@/features/proposals/proposal-info-context';
import { useLineItemBillableRevenue } from '@/features/line-items/line-item-form/route/use-line-item-billable-revenue';
import { useCaptureLineItemCreate } from '@/utils/posthog/analytics';
import { trpc } from '@/utils/trpc-client';
import { StandardLineItemForm } from '../standard/standard-line-item-form';
import { MgmtFeeLineItemForm } from '../mgmt-fee/mgmt-fee-line-item-form';
import { ZeroDollarLineItemForm } from '../zero-dollar/zero-dollar-line-item-form';
import {
  createInitialMgmtFeeState,
  createInitialStandardState,
  createInitialZeroState,
  createMgmtFeeFromExisting,
  createStandardFromExisting,
  createZeroDollarFromExisting
} from '@/features/line-items/line-item-form/common/form-state';

type Props = {
  onClose: () => void;
};

export function NewLineItemFormSelectorV3({ onClose }: Props) {
  const { proposal } = useProposalContext();
  const { channels } = useChannelRatesContext();
  const proposalInfo = useProposalInfo();
  const [lineItemType, setLineItemType] = useState<LineItemType>('standard');
  const [isDirty, setIsDirty] = useState(false);
  const { mutateAsync, isPending } = trpc.createLineItem.useMutation();
  const utils = trpc.useUtils();
  const captureCreate = useCaptureLineItemCreate();

  if (!proposal.start_date || !proposal.end_date)
    throw new Error('Strategy must have start and end dates to create a line item');

  async function handleCreate(request: LineItemApiCreateRequest) {
    if (isPending) return;
    const created = await mutateAsync({ lineItem: request, proposal_id: proposal.id });
    captureCreate(created.id, 'new');
    await utils.getProposalLineItems.invalidate();
    await utils.campaignV2.invalidate();
    await utils.proposalDiff.invalidate();
    onClose();
  }

  const typeSelector = (
    <LineItemTypeSelector
      lineItemType={lineItemType}
      setLineItemType={setLineItemType}
      formIsDirty={isDirty}
    />
  );

  return (
    <Card className="h-full overflow-auto">
      {lineItemType === 'standard' && (
        <StandardLineItemForm
          key="standard"
          initialState={createInitialStandardState(channels, proposalInfo)}
          onSubmit={handleCreate}
          onCancel={onClose}
          onFormChange={setIsDirty}
          typeSelector={typeSelector}
        />
      )}
      {lineItemType === 'management_fee' && (
        <MgmtFeeLineItemForm
          key="management_fee"
          initialState={createInitialMgmtFeeState(channels, proposalInfo)}
          onSubmit={handleCreate}
          onCancel={onClose}
          onFormChange={setIsDirty}
          typeSelector={typeSelector}
        />
      )}
      {lineItemType === 'zero_dollar' && (
        <ZeroDollarLineItemForm
          key="zero_dollar"
          initialState={createInitialZeroState(channels, proposalInfo)}
          onSubmit={handleCreate}
          onCancel={onClose}
          onFormChange={setIsDirty}
          typeSelector={typeSelector}
        />
      )}
    </Card>
  );
}

type EditProps = Props & { lineItem: LineItemTrpcResponse };

export function EditLineItemFormSelectorV3({ onClose, lineItem }: EditProps) {
  const { proposal } = useProposalContext();
  const { channels } = useChannelRatesContext();
  const proposalInfo = useProposalInfo();
  const billableRevenue = useLineItemBillableRevenue(lineItem);
  const { mutateAsync, isPending } = trpc.updateProposalLineItem.useMutation();
  const utils = trpc.useUtils();

  if (!proposal.start_date || !proposal.end_date)
    throw new Error('Strategy must have start and end dates to edit a line item');

  if (billableRevenue.isLoading) return null;

  async function handleEdit(request: LineItemApiCreateRequest) {
    if (isPending) return;
    await mutateAsync({
      strategy_id: proposal.id,
      line_item_id: lineItem.id,
      lineItem: request as ProposalLineItemApiUpdateRequest
    });
    await utils.getProposalLineItems.invalidate();
    await utils.campaignV2.invalidate();
    await utils.proposalDiff.invalidate();
    onClose();
  }

  if (lineItem.line_item_type === 'standard') {
    return (
      <Card className="h-full overflow-auto @container">
        <StandardLineItemForm
          initialState={createStandardFromExisting(
            channels,
            proposalInfo,
            lineItem,
            billableRevenue.rows
          )}
          onSubmit={handleEdit}
          onCancel={onClose}
          typeSelector={<LineItemTypeIndicator lineItemType={lineItem.line_item_type} />}
        />
      </Card>
    );
  }
  if (lineItem.line_item_type === 'management_fee') {
    return (
      <Card className="h-full overflow-auto @container">
        <MgmtFeeLineItemForm
          initialState={createMgmtFeeFromExisting(
            channels,
            proposalInfo,
            lineItem,
            billableRevenue.rows
          )}
          onSubmit={handleEdit}
          onCancel={onClose}
          typeSelector={<LineItemTypeIndicator lineItemType={lineItem.line_item_type} />}
        />
      </Card>
    );
  }
  return (
    <Card className="h-full overflow-auto @container">
      <ZeroDollarLineItemForm
        initialState={createZeroDollarFromExisting(
          channels,
          proposalInfo,
          lineItem,
          billableRevenue.rows
        )}
        onSubmit={handleEdit}
        onCancel={onClose}
        typeSelector={<LineItemTypeIndicator lineItemType={lineItem.line_item_type} />}
      />
    </Card>
  );
}

type SelectorProps = {
  formIsDirty: boolean;
  lineItemType: LineItemType;
  setLineItemType: (type: LineItemType) => void;
};

function LineItemTypeSelector({ formIsDirty, lineItemType, setLineItemType }: SelectorProps) {
  function handleSelect(type: LineItemType) {
    if (formIsDirty) {
      const proceed = window.confirm(
        'Switching line item types will clear any inputted values. Are you sure?'
      );
      if (proceed) setLineItemType(type);
    } else setLineItemType(type);
  }

  return (
    <Select value={lineItemType} onValueChange={(value: LineItemType) => handleSelect(value)}>
      <SelectTrigger
        className="w-[190px] rounded bg-gray-800 text-sm text-white"
        data-testid="line-item-type-selector">
        <SelectValue placeholder="Select a type" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Line Item Types</SelectLabel>
          <SelectItem value="standard">Standard Line Item</SelectItem>
          <SelectItem value="management_fee">MGMT Fee Line Item</SelectItem>
          <SelectItem value="zero_dollar">Zero Dollar Line Item</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
